import { Colli } from '@frontend/colli/types';
import { Package, PackageType } from '@frontend/package/types';
import { Transaction } from '@frontend/transaction/types';
import type { IUser } from '@frontend/user/types';
import type { MobileWizardStateRecord } from '@frontend/wizard/mobile';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification, useScanners } from '../../../hooks';
import { useOpenDropOff } from '../../hooks/useOpenDropOff';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { transactionToTransactionExtended, useWizardTransaction } from '../../hooks/useWizardTransaction';
import { resolveCourierSender } from '../../utils/resolve-courier-sender';
import { resolveDropOffColliContext, resolveDropOffPackageContext } from '../../utils/seed-drop-off-colli-context';
import { useScreen } from '../useScreen';

interface ViewProps {
    resolving: boolean;
    onSubmit: () => void;
    submitDisabled: boolean;
    onBack: (screenType: ScreenType) => void;
    error: string | null;
    resetError: () => void;
}

const SENSOR_TIMEOUT = 10000;

const findSubmitStep = (store: MobileWizardStateRecord | null | undefined): Step | undefined => {
    if (!store) return undefined;
    const submitTrue = store.steps.find((step) => step.screen === ScreenType.DROP_OFF_SCAN_QR_CODE && step.trigger === ScreenTrigger.SUBMIT_TRUE);
    if (submitTrue) return submitTrue;
    return store.steps.find((step) => step.screen === ScreenType.DROP_OFF_SCAN_QR_CODE && step.trigger === ScreenTrigger.SUBMIT);
};

const useDropOffScanQrCode = (): ViewProps => {
    const intl = useIntl();
    const { navigationState, store, onBack, onSubmit: onContinue, updateWizardData } = useScreen();
    const { isOpenDropOffEnabled, enterOpenDropOff } = useOpenDropOff();
    const wizardTransaction = useWizardTransaction({
        wizardId: store!.wizard.id,
        store,
        autoFetch: false,
        autoCreate: false
    });
    const { package: _package, localUpdatePackage } = useWizardPackage({
        wizardId: store!.wizard.id,
        store,
        autoFetch: false,
        autoCreate: false
    });

    const [resolving, setResolving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastScannedCodeRef = useRef<string | undefined>(undefined);

    const clearSensorTimeout = () => {
        if (sensorTimeoutRef.current) {
            clearTimeout(sensorTimeoutRef.current);
            sensorTimeoutRef.current = null;
        }
    };

    const onFail = () => {
        clearSensorTimeout();
        setResolving(false);
        setError(intl.formatMessage({ id: 'HOME.ERRORS.CODE_NOT_FOUND', defaultMessage: 'No matching identification found. Please try again.' }));
    };

    const goToOpenDropOff = async (options: { user?: IUser; shippingNotes?: string }) => {
        clearSensorTimeout();

        const falseStep = store?.steps.find((step) => step.screen === ScreenType.DROP_OFF_SCAN_QR_CODE && step.trigger === ScreenTrigger.SUBMIT_FALSE);
        if (!store || !isOpenDropOffEnabled || !falseStep) {
            onFail();
            return;
        }

        setResolving(true);
        setError(null);

        try {
            const settings = await enterOpenDropOff();
            if (!settings) {
                onFail();
                return;
            }

            const shippingNotes = options.shippingNotes?.trim() || undefined;
            const transactionPatch: { shipping_notes?: string } = {};
            if (shippingNotes) {
                transactionPatch.shipping_notes = shippingNotes;
            }
            if (Object.keys(transactionPatch).length > 0) {
                await wizardTransaction.localUpdateTransaction(transactionPatch);
            }

            const wizardDataPatch: { courierTrackingNumber?: string; senders?: unknown[] } = {};
            if (shippingNotes) {
                wizardDataPatch.courierTrackingNumber = shippingNotes;
            }

            if (options.user) {
                const sender = await resolveCourierSender(options.user, settings, store.wizard.account_id, store.data.transaction?.id);
                if (sender) {
                    wizardDataPatch.senders = [sender];
                }
            }

            if (Object.keys(wizardDataPatch).length > 0) {
                updateWizardData(wizardDataPatch);
            }

            setResolving(false);
            onContinue(falseStep);
        } catch (err) {
            console.error('Failed to continue to open drop-off from drop-off scan QR:', err);
            onFail();
        }
    };

    useScanners({
        id: 'DROPOFF_SCAN_QR_CODE',
        onDeviceSensorReceived: (code) => {
            lastScannedCodeRef.current = code;
            setResolving(true);
            clearSensorTimeout();
            sensorTimeoutRef.current = setTimeout(() => {
                setResolving(false);
                setError(intl.formatMessage({ id: 'HOME.ERRORS.CODE_NOT_FOUND', defaultMessage: 'No matching identification found. Please try again.' }));
                sensorTimeoutRef.current = null;
            }, SENSOR_TIMEOUT);
        }
    });

    useIdentification({
        id: 'DROPOFF_SCAN_QR_CODE',
        onNoMatchingIdentificationFound: () => {
            void goToOpenDropOff({ shippingNotes: lastScannedCodeRef.current });
        },
        onUnlinkedIdentificationFound: () => {
            clearSensorTimeout();
            setResolving(false);
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.drop-off-scan-qr-code.unlinked-identification',
                    description: 'Error shown when a scanned code is recognized but not linked to anything',
                    defaultMessage: 'This code was recognized but is not linked to anything yet. Please try a different code or contact support.'
                })
            );
        },
        onUserIdentified: () => {
            onFail();
        },
        onUserCourierIdentified: (user, trackingNumber) => {
            void goToOpenDropOff({ user, shippingNotes: trackingNumber });
        },
        onPackageIdentified: async (pkg: Package) => {
            if (pkg.type !== PackageType.DROP_OFF) {
                onFail();
                return;
            }
            clearSensorTimeout();

            if (!pkg.transaction_id || !navigationState.iot?.account_id) {
                onFail();
                return;
            }

            try {
                const { transaction, collis, packages } = await resolveDropOffPackageContext({
                    accountId: navigationState.iot.account_id,
                    spotId: navigationState.spot?.id ?? navigationState.iot.spot_id,
                    package: pkg
                });

                await wizardTransaction.localUpdateTransaction(transaction);
                updateWizardData({ collis, packages });
                if (packages[0]) {
                    await localUpdatePackage(packages[0]);
                } else {
                    await localUpdatePackage(pkg);
                }

                setResolving(false);
                onContinue(findSubmitStep(store));
            } catch (err) {
                console.error('Failed to resolve package drop-off context:', err);
                onFail();
            }
        },
        onColliIdentified: async (colli: Colli) => {
            clearSensorTimeout();

            if (!colli?.transaction_id || !navigationState.iot?.account_id) {
                onFail();
                return;
            }

            try {
                const { transaction, collis, packages } = await resolveDropOffColliContext({
                    accountId: navigationState.iot.account_id,
                    spotId: navigationState.spot?.id ?? navigationState.iot.spot_id,
                    colli
                });

                await wizardTransaction.localUpdateTransaction(transaction);
                updateWizardData({ collis, packages });
                if (packages[0]) {
                    await localUpdatePackage(packages[0]);
                }

                setResolving(false);
                onContinue(findSubmitStep(store));
            } catch (err) {
                console.error('Failed to resolve colli drop-off context:', err);
                onFail();
            }
        },
        onTransactionIdentified: (transaction: Transaction) => {
            clearSensorTimeout();

            wizardTransaction.localUpdateTransaction(transactionToTransactionExtended(transaction));
            setResolving(false);
            onContinue(findSubmitStep(store));
        }
    });

    const submitDisabled = (!_package?.id && !wizardTransaction.transaction?.id && !(store?.data.collis?.length > 0)) || resolving || !store;

    const onSubmit = () => {
        if (!submitDisabled && store) {
            updateWizardData({ package_type: PackageType.DROP_OFF });
            onContinue(findSubmitStep(store));
        }
    };

    useEffect(() => {
        return () => {
            clearSensorTimeout();
        };
    }, []);

    return {
        resolving,
        onSubmit,
        submitDisabled,
        onBack,
        error,
        resetError: () => setError(null)
    };
};

export default useDropOffScanQrCode;
